/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {PERMISSIONS} from '../../../components/auth/auth-constants';
import {UserProfileSaverService} from '../../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../../common/controller/signal-admin-resource-title.component';
import {ListDefaultActionsComponent} from '../../common/directives/list-default-actions.component';
import {stateTitleView} from '../../common/directives/html/state-title.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../../routing/state-utils';
import {
  ExternalDemandSourceApiService,
  ExternalDemandSourceModel
} from './external-demand-source-api.service';
import {ExternalDemandSourceItemPageComponent} from './external-demand-source-item-page.component';
import {ExternalDemandSourceListPageComponent} from './external-demand-source-list-page.component';

export function externalDemandSourceStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(ExternalDemandSourceApiService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const readOnly = () => !permissions.hasNetwork(PERMISSIONS.manageSystem.apiKey)
    && !permissions.hasDataNetworkPermission(PERMISSIONS.dataAdmin.apiKey)
    && !permissions.hasNetworkPermission(PERMISSIONS.politicalAdmin.apiKey, null, 'POLITICAL');
  const rh = adnRouterHelper.createRouterHelper(
    'external-demand-sources.external-demand-source',
    ExternalDemandSourceApiService,
    'app.adm',
    'app/admin'
  );

  states.add(rh.listPath(), rh.list({
    resolve: {listReadOnly: readOnly},
    views: {
      'content@app': {
        component: ExternalDemandSourceListPageComponent,
        bindings: {readOnly: 'listReadOnly'}
      },
      'context@app': {
        component: ListDefaultActionsComponent,
        bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}
      },
      'title@app': stateTitleView()
    }
  }));

  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    resolve: {
      externalDemandSourcePage: function($stateParams: any) {
        if ($stateParams.isNew) {
          return new SignalAdminResourcePageState<ExternalDemandSourceModel>(
            {id: $stateParams.id, objectState: 'ACTIVE', validationWarnings: []},
            true,
            readOnly(),
            api,
            'External Demand Source'
          );
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(
          model,
          false,
          readOnly(),
          api,
          'External Demand Source'
        ));
      },
      model: function(externalDemandSourcePage: SignalAdminResourcePageState<ExternalDemandSourceModel>) {
        return externalDemandSourcePage.model();
      }
    },
    views: {
      'content@app': {
        component: ExternalDemandSourceItemPageComponent,
        bindings: {state: 'externalDemandSourcePage'}
      },
      'context@app': {
        component: SignalAdminResourceContextActionsComponent,
        bindings: {state: 'externalDemandSourcePage'}
      },
      'title@app': {
        component: SignalAdminResourceTitleComponent,
        bindings: {state: 'externalDemandSourcePage'}
      }
    }
  }));

  return states.build();
}
